import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from '@mui/material';
import * as XLSX from 'xlsx';
import LoadingProgressBar from '../Loading-ProgressBar';
import ErrorMsgPopup from '../modal-popup/errormsgpopup';
import transformColumnsForTanStack from '../extendedcommondatagrid/transformColumnsForTanStack';
// ---------------------------------------------------------------------------------------------------------

ExportToExcelButton.propTypes = {
    rowData: PropTypes.array,
    columnData: PropTypes.array,
};
// ---------------------------------------------------------------------------------------------------------

export default function ExportToExcelButton({ rowData, columnData }) {

    const [isExporting, setIsExporting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [errorPopup, setErrorPopup] = useState(false);

    const handelErrorPopup = () => {
        setErrorMsg('');
        setErrorPopup(false);
    };

    // Detect if incoming columns are from MUI DataGrid
    const isMuiGridColumns = (cols = []) => cols?.some(col => col.field && !col.accessorKey);

    const buildCellParamsForExport = (value, row) => ({
        value,
        row,
        getValue: () => value
    });

    const formatDateValue = (value) => {
        if (!value) return "";

        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return value;

        const pad = (n) => String(n).padStart(2, "0");
        const day = pad(d.getDate());
        const month = d.toLocaleString("en-US", { month: "short" });
        const year = d.getFullYear();
        const hh = pad(d.getHours());
        const mm = pad(d.getMinutes());
        const ss = pad(d.getSeconds());

        const hasTime = typeof value === "string" && /\d{1,2}:\d{2}(:\d{2})?/.test(value);

        return hasTime ? `${day}-${month}-${year} ${hh}:${mm}:${ss}` : `${day}-${month}-${year}`;
    };

    const exportToExcel = async () => {
        setIsExporting(true);
        try {
            await new Promise((resolve) => setTimeout(resolve, 500));

            // Convert MUI columns ONLY when required
            const finalColumns = isMuiGridColumns(columnData) ? transformColumnsForTanStack(columnData, rowData) : columnData;

            const filteredColumns = finalColumns.filter((col) =>
                !["actions", "__check__", "id"].includes(col.accessorKey) &&
                (col.showInGrid !== false || col.exportable)
            );

            const sanitizedColumns = filteredColumns.map((col) => ({
                ...col,
                header: (col.header || "").replace(/\n/g, " "),
            }));

            const exportHeaders = sanitizedColumns.map((col) => col.header || col.accessorKey);
            const exportFields = sanitizedColumns.map((col) => col.accessorKey);

            const workbook = XLSX.utils.book_new();
            const worksheet = XLSX.utils.aoa_to_sheet([]);
            XLSX.utils.sheet_add_aoa(worksheet, [exportHeaders], { origin: "A1" });
            const currentRow = 1;

            const keywordsRegex = /value|amount|price|stdnr|rate|actualnr|totaldiff|items|quantity|qty|stock|purchase|return|adjustment|inword|outword|jnjsale|interdealersale|unitgts|subtotal|inward|outward|conversionFactor|territorySeqNo/i;
            const excludeKeyword = "Date";

            const reorderedData = rowData.map((item) => {
                const reorderedItem = [];

                exportFields.forEach((key) => {
                    const col = sanitizedColumns.find((column) => column.accessorKey === key);
                    if (!col) {
                        reorderedItem.push("");
                        return;
                    }

                    const value = item[key];
                    const rendererType = col.meta?.rendererType;
                    let resultValue = value;

                    try {
                        // Handle renderCell
                        if (rendererType === "renderCell" && typeof col.cell === "function") {
                            const renderedValue = col.cell(buildCellParamsForExport(value, item));

                            if (React.isValidElement(renderedValue)) {
                                const child = renderedValue.props?.children;
                                resultValue = formatDateValue(child ?? value);
                            } else {
                                resultValue = formatDateValue(renderedValue ?? value);
                            }
                        }
                        // Handle valueFormatter or date columns
                        else if (rendererType === "valueFormatter" && typeof col.cell === "function") {
                            const headerNameMatches = col?.header && keywordsRegex.test(col.header) && col.header !== excludeKeyword;
                            const fieldMatches = col?.accessorKey && keywordsRegex.test(col.accessorKey) && col.accessorKey !== excludeKeyword;
                            const isDateColumn = /date/i.test(col.header || "") || /date/i.test(col.accessorKey || "");

                            if (isDateColumn) {
                                try {
                                    const formatted = col.cell(buildCellParamsForExport(value, item));

                                    if (React.isValidElement(formatted)) {
                                        const child = formatted.props?.children;
                                        resultValue = formatDateValue(child ?? value);
                                    } else {
                                        resultValue = formatDateValue(formatted ?? value);
                                    }
                                } catch (err) {
                                    console.log('err', err)
                                    resultValue = value;
                                }
                            }
                            else if (headerNameMatches || fieldMatches) {
                                resultValue = value;
                            }
                            else if (/time/i.test(col.header || col.accessorKey)) {
                                if (typeof value === "string") {
                                    // Remove milliseconds if present
                                    resultValue = value.split(".")[0];
                                } else {
                                    resultValue = value ?? "";
                                }
                            }
                            else {
                                resultValue = col.cell({ value, row: item });
                            }
                        }

                        // Numeric cleanup
                        if (!Number.isNaN(Number(resultValue)) && resultValue !== null && resultValue !== "") {
                            reorderedItem.push(resultValue);
                        } else {
                            reorderedItem.push(resultValue ?? "");
                        }
                    } catch (err) {
                        reorderedItem.push("");
                    }
                });

                return reorderedItem;
            });

            XLSX.utils.sheet_add_aoa(worksheet, reorderedData, { origin: { r: currentRow, c: 0 } });

            // Apply number formatting
            if (worksheet["!ref"]) {
                const range = XLSX.utils.decode_range(worksheet["!ref"]);

                for (let c = 0; c < exportFields.length; c += 1) {
                    const col = sanitizedColumns[c];
                    for (let r = 1; r <= range.e.r; r += 1) {
                        const cellAddress = XLSX.utils.encode_cell({ r, c });
                        const cell = worksheet[cellAddress];

                        if (!cell) {
                            // Skip processing for empty cell
                            return;
                        }

                        const headerNameMatches =
                            col?.header && keywordsRegex.test(col.header) && col.header !== excludeKeyword;
                        const fieldMatches =
                            col?.accessorKey && keywordsRegex.test(col.accessorKey) && col.accessorKey !== excludeKeyword;

                        if (headerNameMatches || fieldMatches) {
                            const numericValue = parseFloat(String(cell.v).replace(/,/g, ""));
                            if (!Number.isNaN(numericValue)) {
                                cell.v = numericValue;
                                cell.t = "n";
                                cell.z = ["conversionFactor", "territorySeqNo"].includes(col.accessorKey)
                                    ? "0"
                                    : "#,##0.00";
                            }
                        }

                        if (col?.accessorKey === "dos") {
                            cell.t = "n";
                            cell.z = "#,##0";
                        }

                        if (col?.accessorKey === "distributorMargin") {
                            const numericValue = parseFloat(String(cell.v).replace("%", "")) / 100;
                            if (!Number.isNaN(numericValue)) {
                                cell.v = numericValue;
                                cell.t = "n";
                                cell.z = "0.00%";
                            }
                        }

                        if (col?.accessorKey === "distributorCode") {
                            const numericValue = parseFloat(cell.v);
                            if (!Number.isNaN(numericValue)) {
                                cell.v = numericValue;
                                cell.t = "n";
                                cell.z = "0";
                            }
                        }
                    }
                }
            }

            XLSX.utils.book_append_sheet(workbook, worksheet, "Data");

            const pageTitle = document.title;
            const formattedDate = new Date().toISOString().slice(0, 10);
            const fileName = `${pageTitle}_${formattedDate}.xlsx`;

            XLSX.writeFile(workbook, fileName);
        } catch (error) {
            console.error("Error exporting data to Excel:", error);
            setErrorMsg("Error exporting data to Excel. Please try again.");
            setErrorPopup(true);
        } finally {
            setIsExporting(false);
        }
    };

    return (
        <>
            <Button className='buttonStyle' variant='contained' size="small"
                sx={{ color: '#fff !important' }} onClick={exportToExcel}>
                Export
            </Button>

            {isExporting && <LoadingProgressBar />}

            {errorPopup && <ErrorMsgPopup confirm={handelErrorPopup} msg={errorMsg} header="Error" />}
        </>
    );
}